import React, { useState, useReducer, useEffect, useRef } from 'react';
import DashboardTab from './tabs/DashboardTab';
import DiscoverTab from './tabs/DiscoverTab';
import ConnectTab from './tabs/ConnectTab';
import ThemesTab from './tabs/themesTab/ThemesTab';
import ReactMarkdown from 'react-markdown';
import FollowingTab from './tabs/followingTab/FollowingTab';

import * as api from '../../../api/FollowsListAPI';
import SidebarContainer from './SidebarContainer';

const listReducer = (curList, action) => {
	switch (action.type) {
		case 'FETCH_ALL':
			return action.payload;
		case 'CREATE':
			return [...curList, action.payload];
		case 'DELETE':
			return curList.filter(
				(followsList) => followsList.id != action.payload
			);
		case 'UPDATE':
			return curList.map((followsList) =>
				followsList.id == action.payload.id
					? action.payload
					: followsList
			);
		default:
			return curList;
	}
};

const isFollowed = (followsLists, kind, id) => {
	// check if this article is in a list
	// for each list
	for (i = 0; i < followsLists.length; i++) {
		// for each article in the list
		let ids = followsLists[i][kind + 's'].map((data) => data[kind].id);
		if (ids.includes(id)) {
			return true;
		}

		if (i == followsLists.length - 1) {
			return false;
		}
	}
};

const index = ({
	tabs,
	is_new_user,
	is_subscribed,
	organization,
	dashboard_props,
	discover_props,
	following_props,
	connect_props,
	tracker_props,
	sidebar_props,
	themes_props,
}) => {
	const [followsLists, listDispatch] = useReducer(listReducer, []);
	const [tabState, setTabState] = useState(1);
	const isMounted = useRef(null);

	// This is from dashboard/discover tabs
	const [articleData, setArticleData] = useState('');
	const [organizationData, setOrganizationData] = useState('');
	const [dashboardFollowsLists, setFollowsLists] = useState(followsLists);
	// End

	const fetchLists = async () => {
		try {
			const { data } = await api.fetchAllLists();
			if (isMounted.current) {
				listDispatch({ type: 'FETCH_ALL', payload: data });
			}
		} catch (error) {
			console.error(error);
		}
	};

	useEffect(() => {
		isMounted.current = true;
		fetchLists();
		return () => (isMounted.current = false);
	}, []);

	/**
	 * Method to create a new list and then follow the current article or organization, appending to that new list.
	 * @param data The article or organization that will be followed.
	 * @param isArticle True if it's an article, false if it's an organization to be followed.
	 */
	const createListAndFollow = async (data, isArticle) => {
		let newListName = document.getElementById('new-list-input-box').value;
		let newListNameObject = { name: newListName };

		try {
			// Must first create a list and get the new list ID given by the DB
			let returnData = {};
			await api
				.createList(newListNameObject)
				.then((response) => {
					returnData = response.data;
				})
				.catch((error) => {
					console.error(error);
				});

			let newFollowsList = returnData;
			newFollowsList.organizations = [];
			newFollowsList.articles = [];
			listDispatch({ type: 'CREATE', payload: newFollowsList });

			let followsListToUpload;
			if (isArticle) {
				let articleIds = newFollowsList.articles.map(
					(data) => data.article.id
				);
				articleIds.push(data.article.id);
				newFollowsList.articles.push(data);

				followsListToUpload = {
					name: newFollowsList.name,
					description: newFollowsList.description,
					article_ids: articleIds,
				};
				setArticleData('');
			} else {
				let organizationIds = newFollowsList.organizations.map(
					(data) => data.organization.id
				);
				organizationIds.push(data.organization.id);
				newFollowsList.organizations.push(data);

				followsListToUpload = {
					name: newFollowsList.name,
					description: newFollowsList.description,
					organization_ids: organizationIds,
				};
				setOrganizationData('');
			}

			setFollowsLists(dashboardFollowsLists);
			// We can set this object's followed state to true, since we've just created this list, and nothing is followed
			data.followed = true;

			try {
				const { data } = await api.editList(
					newFollowsList.id,
					followsListToUpload
				);
				listDispatch({ type: 'UPDATE', payload: data });
			} catch (error) {
				console.error(error);
			}
		} catch (error) {
			console.error(error);
		}
	};

	/**
	 * Method to add or remove the selected article from the user's follow list, depending on if they're currently following.
	 * @param articleData the article to be added or removed.
	 * @param followsList the user's current follow list.
	 */
	const addOrRemoveArticle = async (articleData, followsList) => {
		let articleIds = followsList.articles.map(
			(articleData) => articleData.article.id
		);
		if (articleIds.includes(articleData.article.id)) {
			articleIds = articleIds.filter(
				(item) => item !== articleData.article.id
			);
			followsList.articles = followsList.articles.filter(
				(item) => item.article.id !== articleData.article.id
			);
		} else {
			articleIds.push(articleData.article.id);
			followsList.articles.push(articleData);
		}

		const newList = {
			name: followsList.name,
			description: followsList.description,
			article_ids: articleIds,
		};

		setFollowsLists(followsLists);

		articleData.followed = isFollowed(
			followsLists,
			'article',
			articleData.article.id
		);
		setArticleData('');

		try {
			const { data } = await api.editList(followsList.id, newList);
			listDispatch({ type: 'UPDATE', payload: data });
		} catch (error) {
			console.error(error);
		}
	};

	/**
	 * Method to add or remove the selected org from the user's follow list, depending on if they're currently following.
	 * @param organizationData the organizations to be added to removed.
	 * @param followsList the user's current follow list.
	 */
	const addOrRemoveOrganization = async (organizationData, followsList) => {
		let organizationIds = followsList.organizations.map(
			(organizationData) => organizationData.organization.id
		);
		if (organizationIds.includes(organizationData.organization.id)) {
			organizationIds = organizationIds.filter(
				(item) => item !== organizationData.organization.id
			);
			followsList.organizations = followsList.organizations.filter(
				(item) =>
					item.organization.id !== organizationData.organization.id
			);
		} else {
			organizationIds.push(organizationData.organization.id);
			followsList.organizations.push(organizationData);
		}

		const newList = {
			name: followsList.name,
			description: followsList.description,
			organization_ids: organizationIds,
		};

		setFollowsLists(followsLists);

		organizationData.followed = isFollowed(
			followsLists,
			'organization',
			organizationData.organization.id
		);
		setOrganizationData('');

		try {
			const { data } = await api.editList(followsList.id, newList);
			listDispatch({ type: 'UPDATE', payload: data });
		} catch (error) {
			console.error(error);
		}
	};

	/**
	 * Method to show the modify-list modal for orgs, responsible for following/unfollowing/or creating a new list.
	 */
	const modalContentOrganizations = () => {
		if (!organizationData.organization) {
			return <div />;
		}

		return (
			<div className="modal-section">
				<h3>
					Add item to:
					<i
						id="new-list-close-button"
						className="fas fa-times float-right"
						onClick={() => setOrganizationData('')}
					/>
				</h3>
				{followsLists.map((followsList, index) => {
					let organizationIds = followsList.organizations.map(
						(organizationData) => organizationData.organization.id
					);
					return (
						<h5
							className="name-holder"
							onClick={() =>
								addOrRemoveOrganization(
									organizationData,
									followsList
								)
							}
							key={followsList.id}
						>
							{organizationIds.includes(
								organizationData.organization.id
							) ? (
								<i className="fa fa-minus-circle" />
							) : (
								<i className="fa fa-plus-circle" />
							)}
							<span className="list-name">
								{followsList.name}
							</span>
						</h5>
					);
				})}
				<h6>Add to new list</h6>
				<input
					id="new-list-input-box"
					className="new-list-input-box"
					type="text"
					placeholder="Insert list name here..."
				/>
				<div className="primary-button float-right">
					<input
						className="primary-button"
						type="submit"
						value="Save"
						onClick={() =>
							createListAndFollow(organizationData, false)
						}
					/>
				</div>
			</div>
		);
	};

	const TabContent = () => {
		switch (tabState) {
			case 1:
				return (
					<DashboardTab
						{...dashboard_props}
						lists={followsLists}
						createListAndFollow={createListAndFollow}
						addOrRemoveArticle={addOrRemoveArticle}
						articleData={articleData}
						setArticleData={setArticleData}
						organizationData={organizationData}
						setOrganizationData={setOrganizationData}
						modalContentOrganizations={modalContentOrganizations}
					/>
				);
			case 2:
				return (
					<DiscoverTab
						{...discover_props}
						organizationData={organizationData}
						setOrganizationData={setOrganizationData}
						modalContentOrganizations={modalContentOrganizations}
					/>
				);
			case 3:
				return (
					<FollowingTab
						{...following_props}
						followsLists={followsLists}
						listDispatch={listDispatch}
						api={api}
					/>
				);
			case 4:
				if (is_subscribed && organization) {
					return <ConnectTab {...connect_props} />;
				} else {
					return (
						<div>
							This content is only available to users that are
							connected to an organization
						</div>
					);
				}
			case 5:
				return <div></div>;
			case 6:
				return <SidebarContainer {...sidebar_props} />;
			case 7:
				return <ThemesTab {...themes_props} />;
			default:
				return null;
		}
	};

	return (
		<div className="row" id="dashboard">
			<div className="columns medium-2 hide-for-small-only">
				<SidebarContainer
					{...sidebar_props}
					organization={organization}
				/>
			</div>
			<div className="columns small-12 medium-10">
				<ul className="tabs" id="dashboard-tabs">
					{tabs.map((tab, index) => {
						return (
							<li
								className={
									'tabs-title' +
									(tab.fields['en-US'].sequence == tabState
										? ' is-active'
										: tab.fields['en-US'].sequence == 6
										? ' hide-for-medium'
										: '')
								}
								data-alignment="left"
								data-allow-html="true"
								data-position="bottom"
								title={tab.tooltip}
								key={index}
							>
								<a
									onClick={() =>
										setTabState(
											tab.fields['en-US'].sequence
										)
									}
								>
									{tab.fields['en-US'].title}
								</a>
							</li>
						);
					})}
				</ul>
				{tabs.map((tab, index) => {
					if (tab.fields['en-US'].sequence == tabState) {
						return (
							<div key={index}>
								<ReactMarkdown
									children={tab.fields['en-US'].content}
								/>
								<TabContent />
							</div>
						);
					}
				})}
			</div>
		</div>
	);
};

export default index;
